import { Component, Inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, filter, first, switchMap, tap } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ApiOperation } from '../../shared/models/api-operation.model';
import { Collection } from '../../shared/models/collection.model';

interface DialogData {
  sessions: string[],
  acquisitions: string[],
}

@Component({
  selector: 'add-to-collection-dialog',
  templateUrl: './add-to-collection-dialog.component.html',
  styleUrls: ['./add-to-collection-dialog.component.scss'],
})
export class AddToCollectionDialog {

  state: 'addToCollection' | 'createOnly' = 'addToCollection';

  existingCollections: Collection[] = [];

  form = new FormGroup({
    selectedCollection: new FormControl('', Validators.required),
    newCollectionName: new FormControl({ value: '', disabled: true }),
  });

  newCollectionControl = new FormControl('', [Validators.required, this.uniqueCollectionValidator()]);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialogRef: MatDialogRef<AddToCollectionDialog>,
    private coreApi: CoreApiService,
    private profileService: ProfileService,
    private notification: NotificationService,
  ) {
    if (!data?.sessions.length && !data?.acquisitions.length) {
      this.state = 'createOnly';
    }

    this.profileService.exhaustiveMode$.pipe(
      first(),
      switchMap(exhaustive => this.coreApi.collections.query({ exhaustive })),
    ).subscribe(collections => {
      this.existingCollections = collections;
    });
  }

  existingOrNewChanged({ value }): void {
    const { newCollectionName, selectedCollection } = this.form.controls;
    if (value === 'new') {
      newCollectionName.enable();
      newCollectionName.setValidators([Validators.required, this.uniqueCollectionValidator()]);
      selectedCollection.reset('');
      selectedCollection.disable();
    }
    else if (value === 'existing') {
      selectedCollection.enable();
      selectedCollection.setValidators(Validators.required);
      newCollectionName.reset('');
      newCollectionName.disable();
    }
  }

  createCollection(label: string): Observable<Pick<Collection, '_id'>> {
    return this.coreApi.collections.post({ label }).pipe(
      catchError(() => {
        this.notification.error('collectionsAdmin.createNew.error');
        return of(null);
      }),
      filter(result => Boolean(result)),
      tap(() => this.notification.success('collectionsAdmin.createNew.success')),
    );
  }

  addToCollection(): void {
    const { selectedCollection, newCollectionName } = this.form.value;
    const addBody = {
      contents: {
        operation: 'add',
        nodes: [
          ...(this.data?.sessions || []).map(session => ({ level: 'session', _id: session })),
          ...(this.data?.acquisitions || []).map(acq => ({ level: 'acquisition', _id: acq })),
        ],
      } as ApiOperation,
    };

    if (!this.form.valid) {
      return;
    }
    if (newCollectionName) {
      this.createCollection(newCollectionName).pipe(
        switchMap(created => this.coreApi.collections.put(created._id, addBody)),
      ).subscribe({
        next: () => {
          this.notification.success('collectionsAdmin.addToCollection.success');
          this.dialogRef.close();
        },
        error: () => {
          this.notification.error('collectionsAdmin.addToCollection.error');
        },
      });
    }
    else if (selectedCollection) {
      this.coreApi.collections.put(selectedCollection, addBody).subscribe({
        next: () => {
          this.notification.success('collectionsAdmin.addToCollection.success');
          this.dialogRef.close();
        },
        error: () => {
          this.notification.error('collectionsAdmin.addToCollection.error');
        },
      });
    }
  }

  submitCreateOnly(): void {
    this.createCollection(this.newCollectionControl.value).subscribe(() => this.dialogRef.close());
  }

  uniqueCollectionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.existingCollections.map(collection => collection.label.toLowerCase()).includes(control.value.toLowerCase()) ?
        { unique: { value: control.value } } : null;
    };
  }

}
